<template>
  <div class="smartbox" ref="rootContainer">
    <div class="smartbox__trigger" ref="triggerRef" role="button" tabindex="0" :aria-expanded="isOpen"
      aria-haspopup="dialog" @click="toggle" @keydown.enter.prevent="toggle">
      <slot name="trigger"></slot>
    </div>
    <Transition name="fade">
      <div v-if="isOpen && (isMobile || hasContent)" ref="dropdownRef" :class="[
        'smartbox__container',
        isMobile
          ? 'smartbox__container--mobile'
          : 'smartbox__container--desktop'
      ]" :style="containerStyle" role="dialog" aria-modal="true"
        :aria-labelledby="isHeaderActive ? smartboxTitleId : null" :aria-label="!isHeaderActive ? title : null"
        @keydown.esc="close">
        <div v-if="isMobile" class="smartbox__backdrop" @click="close"></div>
        <div class="smartbox__panel" ref="panelRef" tabindex="-1" role="document">
          <header class="smartbox__header" v-if="isMobile || isHeaderActive">
            <button class="smartbox__close" @click="close" aria-label="Close" type="button">
              <span class="icon icons-angle-left font-28 font-strong"></span>
            </button>
            <h2 class="font-20 font-600 font-main" :id="smartboxTitleId">
              {{ title }}
            </h2>
          </header>
          <div class="smartbox__content" :style="contentStyle">
            <slot name="content"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  emits: ['open', 'close', 'update:OpenBox', 'isMobile'],

  props: {
    title: {
      type: String,
      required: false
    },

    isHeaderActive: {
      type: Boolean,
      default: true
    },

    OpenBox: {
      type: Boolean,
      default: false
    },

    mobileBreakpoint: {
      type: Number,
      default: 768
    },
    hasContent: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isOpen: false,
      isMobile: false,
      lastActiveElement: null,
      dynamicLeft: '0px',

      smartboxTitleId: `smartbox-title-${Math.random()
        .toString(36)
        .slice(2)}`
    };
  },

  computed: {
    contentStyle() {
      if (!this.isMobile) {
        return { height: 'auto', padding: '0px', overflowY: 'visible' };
      }
      return {};
    },

    containerStyle() {
      if (this.isMobile) return {};
      return {
        left: this.dynamicLeft
      };
    }
  },

  mounted() {
    this.checkBreakpoint();
    window.addEventListener('resize', this.checkBreakpoint);
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isOpen && !this.isMobile) {
          this.adjustPosition();
        }
      });
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkBreakpoint);
    document.removeEventListener('keydown', this.trapFocus);
    document.removeEventListener('click', this.handleClickOutside);
    this.toggleBodyScroll(false);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    toggleBodyScroll(preventScroll) {
      if (preventScroll) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    checkBreakpoint() {
      this.isMobile = window
        .matchMedia(`(max-width: ${this.mobileBreakpoint - 1}px)`)
        .matches;

      if (this.isOpen) {
        if (!this.isMobile) {
          this.adjustPosition();
          this.toggleBodyScroll(false);
        } else {
          this.toggleBodyScroll(true);
        }
      }
      this.$emit('isMobile', this.isMobile);
    },

    trapFocus(e) {
      if (!this.isOpen || !this.$refs.panelRef) return;

      const focusableElements =
        this.$refs.panelRef.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      // En Shadow DOM, document.activeElement siempre es el Shadow Host.
      // Necesitamos buscar el elemento realmente enfocado dentro de la raíz.
      const root = this.$el.getRootNode();
      const activeElement = root instanceof ShadowRoot ? root.activeElement : document.activeElement;

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    },

    handleClickOutside(event) {
      if (this.isOpen && !this.isMobile && this.$refs.rootContainer) {
        // En Shadow DOM, usamos composedPath() para verificar si el clic fue interno
        const path = event.composedPath();
        if (!path.includes(this.$refs.rootContainer)) {
          // Al cerrar por clic fuera (por ejemplo al cambiar a otro smartbox),
          // no restauramos el foco para no robarselo al nuevo elemento.
          this.close({ restoreFocus: false });
        }
      }
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    open() {
      this.lastActiveElement = document.activeElement;

      this.isOpen = true;

      document.addEventListener('keydown', this.trapFocus);
      document.addEventListener('click', this.handleClickOutside);

      if (this.isMobile) {
        this.toggleBodyScroll(true);
      }

      this.$nextTick(() => {
        if (this.resizeObserver && this.$refs.dropdownRef) {
          this.resizeObserver.observe(this.$refs.dropdownRef);
        }
        this.adjustPosition();
        this.$emit('open');
      });
    },

    close(options) {
      // Si recibimos un evento u objeto sin la propiedad, por defecto es true
      const shouldRestore =
        options && options.restoreFocus === false ? false : true;

      this.isOpen = false;

      document.removeEventListener('keydown', this.trapFocus);
      document.removeEventListener('click', this.handleClickOutside);

      this.toggleBodyScroll(false);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (shouldRestore && this.lastActiveElement)
        this.lastActiveElement.focus();

      this.$emit('close');
      this.$emit('update:OpenBox', false);
    },

    adjustPosition() {
      if (this.isMobile || !this.isOpen) return;

      const runAdjustment = () => {
        const dropdown = this.$refs.dropdownRef;
        const root = this.$refs.rootContainer;

        // If not in DOM yet or zero width (still rendering), wait for next frame
        if (!dropdown || !root || dropdown.offsetWidth === 0) {
          if (this.isOpen) requestAnimationFrame(runAdjustment);
          return;
        }

        const dropdownRect = dropdown.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;

        let offset = 0;
        const margin = 20;

        const rightEdgeAtZero = rootRect.left + dropdownRect.width;

        if (rightEdgeAtZero > viewportWidth - margin) {
          offset = (viewportWidth - margin) - rightEdgeAtZero;
        }

        if (rootRect.left + offset < margin) {
          offset = margin - rootRect.left;
        }

        this.dynamicLeft = `${offset}px`;
      };

      runAdjustment();
    }
  },

  watch: {
    OpenBox(newVal) {
      if (newVal) {
        this.open();
      } else {
        if (this.isOpen) {
          this.close({ restoreFocus: false });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
    .smartbox {
        position: relative;
        display: inline-block;
        width: 100%;

        h2 {
            margin: 0px;
        }

        .icon {
            display: flex;
        }

        &__trigger {
            cursor: pointer;
            display: block;
            min-width: 0;

            &[aria-expanded="true"] :deep(.field) {
                border: 1px solid transparent;
                box-shadow: 0 0 0 2px var(--border-info);
            }
        }

        &__container {
            z-index: 1000;
        }

        &__container--mobile {
            position: fixed;
            top: 0;
            left: 0;
            width: 100dvw;
            height: 100dvh;
            display: flex;
            align-items: center;
            justify-content: center;

            .smartbox__backdrop {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.5);
                /* Fondo oscuro semitransparente */
            }

            .smartbox__panel {
                position: relative;
                overflow-y: auto;
                width: 100%;
                box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
                background-color: var(--bg-base);
            }
        }

        &__container--desktop {
            position: absolute;
            top: calc(100% + 2px);
            width: max-content;

            .smartbox__panel {
                overflow: hidden;
                background: white;
                border: 1px solid #ddd;
                border-radius: var(--space-16);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                position: relative;
            }
        }

        &__header {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--space-20) var(--space-16);
            border-bottom: 1px solid var(--border-subtle);

            &--disabled {
                display: none;
            }
        }

        &__content {
            height: calc(100dvh - 71px);
            padding: var(--space-16);
            overflow-y: auto;
        }

        &__close {
            position: absolute;
            top: 20px;
            left: 16px;
            background: transparent;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            line-height: 1;
            color: #666;

            &:hover {
                color: #000;
            }
        }
    }

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
